import {
  Chip,
  ListSubheader,
  MenuItem,
  Radio,
  Stack,
  Checkbox,
  Select,
  SelectChangeEvent,
  FormControl,
  InputLabel,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  MouseEvent,
  PointerEvent,
  TouchEvent,
  useState,
  useEffect,
} from 'react';
import {
  IncidentMetadataFilter,
  IncidentOwnershipFilter,
  IncidentUrgencyFilter,
  OWNERSHIP_OPTIONS,
  URGENCY_OPTIONS,
  METADATA_OPTIONS,
} from 'hooks/useMapSearch';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { useIncidentFiltersState } from '../../../state/useIncidentFiltersState';
import { useSearchDrawerState } from '../../../state/useSearchDrawerState';

type FilterSelectorProps = {
  disabled?: boolean;
  /**
   * Called on pointer down, before the control takes focus from the search
   * input (which hides the on-screen keyboard). Lets the parent keep the mobile
   * bottom sheet from collapsing when the keyboard hides.
   */
  onBeforeFocusDrawerElement?: () => void;
};

export const FilterSelector = (props: FilterSelectorProps): JSX.Element => {
  const { disabled = false, onBeforeFocusDrawerElement } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const isLargeMediaQuery = useMediaQuery(theme.breakpoints.up('tablet'));
  const { filters, setFilters } = useIncidentFiltersState();
  const { maximized: drawerMaximized } = useSearchDrawerState();
  const [isOpen, setIsOpen] = useState(false);
  const isInternalTabEnabled = useReleaseFlag('internal_tab_on_incidents');
  const metadataOptions = METADATA_OPTIONS.filter(
    (option) =>
      option.value !==
      (isInternalTabEnabled ? 'has_recent_notes' : 'has_active_notes'),
  );

  const selectedValues = [
    ...(filters.ownership ? [`ownership:${filters.ownership}`] : []),
    ...filters.urgency.map((f) => `urgency:${f}`),
    ...filters.metadata.map((f) => `metadata:${f}`),
  ];

  useEffect(() => {
    if (!isLargeMediaQuery && !drawerMaximized && isOpen) {
      setIsOpen(false);
    }
  }, [drawerMaximized, isLargeMediaQuery, isOpen]);

  const handleClose = (): void => {
    setIsOpen(false);
  };

  const handleChange = (event: SelectChangeEvent<string[]>): void => {
    const values = event.target.value as string[];

    let newOwnership: IncidentOwnershipFilter | null = null;
    const newUrgencyFilters: IncidentUrgencyFilter[] = [];
    const newMetadataFilters: IncidentMetadataFilter[] = [];

    values.forEach((value) => {
      const [type, filterValue] = value.split(':');
      if (type === 'ownership') {
        newOwnership = (filterValue as IncidentOwnershipFilter) || null;
      } else if (type === 'urgency') {
        newUrgencyFilters.push(filterValue as IncidentUrgencyFilter);
      } else if (type === 'metadata') {
        newMetadataFilters.push(filterValue as IncidentMetadataFilter);
      }
    });

    setFilters({
      ownership: newOwnership,
      urgency: newUrgencyFilters,
      metadata: newMetadataFilters,
    });
  };

  const handleRemoveOwnership = (): void => {
    setFilters((prev) => ({ ...prev, ownership: null }));
  };

  const handleRemoveUrgency = (filter: IncidentUrgencyFilter): void => {
    const newFilters = filters.urgency.filter((f) => f !== filter);
    setFilters((prev) => ({ ...prev, urgency: newFilters }));
  };

  const handleRemoveMetadata = (filter: IncidentMetadataFilter): void => {
    const newFilters = filters.metadata.filter((f) => f !== filter);
    setFilters((prev) => ({ ...prev, metadata: newFilters }));
  };

  // pointer-down fires before the input blurs and the keyboard hides
  const handleChipDeletePointerDown = (
    e: PointerEvent<HTMLDivElement>,
  ): void => {
    e.stopPropagation();
    onBeforeFocusDrawerElement?.();
  };

  // The Select opens on mouse-down; stop the chip's mouse-down from bubbling so
  // deleting a chip doesn't also open the menu.
  const stopChipDeleteMouseDown = (e: MouseEvent<HTMLDivElement>): void => {
    e.stopPropagation();
  };

  const ownershipValue = filters.ownership || '';

  return (
    <FormControl fullWidth disabled={disabled}>
      <InputLabel id="filter-selector-label">
        {t('searchBar.incidentAttributes')}
      </InputLabel>
      <Select
        multiple
        id="filter-selector"
        labelId="filter-selector-label"
        label={t('searchBar.incidentAttributes')}
        value={selectedValues}
        onChange={handleChange}
        disabled={disabled}
        open={isOpen}
        onOpen={() => setIsOpen(true)}
        onClose={handleClose}
        onPointerDown={onBeforeFocusDrawerElement}
        MenuProps={{
          slotProps: {
            paper: {
              sx: { maxHeight: '70vh' },
              onTouchMove: (e: TouchEvent<HTMLDivElement>) => {
                // This allows scrolling within the menu on mobile without
                // triggering the background to scroll.
                e.preventDefault();
                e.stopPropagation();
              },
            },
            root: {
              slotProps: {
                backdrop: {
                  sx: {
                    backgroundColor: 'background.backdrop',
                  },
                },
              },
            },
          },
        }}
        renderValue={() => (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {filters.ownership && (
              <Chip
                label={t(
                  OWNERSHIP_OPTIONS.find(
                    (opt) => opt.value === filters.ownership,
                  )?.transLabelKey ?? '',
                )}
                onDelete={handleRemoveOwnership}
                onPointerDown={handleChipDeletePointerDown}
                onMouseDown={stopChipDeleteMouseDown}
              />
            )}
            {filters.urgency.map((filter) => (
              <Chip
                key={filter}
                label={t(
                  URGENCY_OPTIONS.find((opt) => opt.value === filter)
                    ?.transLabelKey ?? '',
                )}
                onDelete={() => handleRemoveUrgency(filter)}
                onPointerDown={handleChipDeletePointerDown}
                onMouseDown={stopChipDeleteMouseDown}
              />
            ))}
            {filters.metadata.map((filter) => (
              <Chip
                key={filter}
                label={t(
                  METADATA_OPTIONS.find((opt) => opt.value === filter)
                    ?.transLabelKey ?? '',
                )}
                onDelete={() => handleRemoveMetadata(filter)}
                onPointerDown={handleChipDeletePointerDown}
                onMouseDown={stopChipDeleteMouseDown}
              />
            ))}
          </Box>
        )}
      >
        <Stack
          alignItems="end"
          position="sticky"
          top={0}
          marginTop="-8px"
          borderBottom={1}
          sx={(_theme) => ({
            zIndex: _theme.zIndex.drawer,
            backgroundColor: _theme.palette.background.paper,
            borderColor: 'divider',
          })}
        >
          <IconButton
            size="small"
            onClick={handleClose}
            aria-label={t('searchBar.closeFilters')}
            sx={{
              borderRadius: 0,
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Stack>
        <ListSubheader
          color="inherit"
          sx={{ fontSize: '1rem', position: 'static' }}
        >
          {t('searchBar.ownership')}
        </ListSubheader>
        {OWNERSHIP_OPTIONS.map((option) => (
          <MenuItem
            disableRipple
            key={option.value}
            value={`ownership:${option.value}`}
          >
            <Radio
              checked={ownershipValue === option.value}
              sx={{ mr: 1, p: 0 }}
            />
            <Typography>{t(option.transLabelKey)}</Typography>
          </MenuItem>
        ))}

        <ListSubheader
          color="inherit"
          sx={{ fontSize: '1rem', position: 'static' }}
        >
          {t('searchBar.urgency')}
        </ListSubheader>
        {URGENCY_OPTIONS.map((option) => (
          <MenuItem
            disableRipple
            key={option.value}
            value={`urgency:${option.value}`}
          >
            <Checkbox
              checked={filters.urgency.includes(option.value)}
              sx={{ mr: 1, p: 0 }}
            />
            <Stack direction="row" spacing={1} alignItems="center">
              {option.icon}
              <Typography>{t(option.transLabelKey)}</Typography>
            </Stack>
          </MenuItem>
        ))}

        <ListSubheader color="inherit" sx={{ fontSize: '1rem' }}>
          {t('searchBar.metadata')}
        </ListSubheader>
        {metadataOptions.map((option) => (
          <MenuItem
            disableRipple
            key={option.value}
            value={`metadata:${option.value}`}
          >
            <Checkbox
              checked={filters.metadata.includes(option.value)}
              sx={{ mr: 1, p: 0 }}
            />
            <Typography>{t(option.transLabelKey)}</Typography>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
